import { Block } from 'components/Shared/Layout';
import { rm } from 'utils';
import { Input, OnChangeHandler, rule, required, isValidEmail } from 'components/Shared/Form';
import { Reducer, useReducer, FormEventHandler } from 'react';
import styles from './styles.scss';
import { Button } from 'components/Shared/Loader';
import { Validator, isValidPhoneNumber } from 'views/CheckoutPage/validation';
import { postKitchenContact } from 'services/ApiProductPageService';
import connect from 'components/Shared/connect';
import { getFullProductId } from 'views/ProductPage/selector';
import { LoadingStatus } from 'models/LoadingStatus';
import { ModalProps } from '..';

interface OwnState {
  name: string;
  phone: string;
  email: string;
  error?: string;
  loadingStatus: LoadingStatus;
}

type OwnProps = Pick<ModalProps, 'setModal'>;

type MergedProps = {
  fullProductId: string;
} & OwnProps;

type FormActionKey = 'name' | 'phone' | 'email';

type Action =
  | {
      key: FormActionKey;
      value: string;
      type: 'setValue';
    }
  | { type: 'setError'; error?: string }
  | { type: 'reset' }
  | { type: 'setLoading' }
  | { type: 'resetLoading' };

const initialState: OwnState = {
  name: '',
  phone: '',
  email: '',
  error: undefined,
  loadingStatus: LoadingStatus.Idle
};

const reducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'setValue':
      return {
        ...state,
        [action.key]: action.value
      };
    case 'setError':
      return {
        ...state,
        error: action.error
      };
    case 'reset':
      return initialState;
    case 'setLoading':
      return { ...state, loadingStatus: LoadingStatus.Fetching };
    case 'resetLoading':
      return { ...state, loadingStatus: LoadingStatus.Idle };
    default:
      return state;
  }
};

const rules: { [K in FormActionKey]: Validator } = {
  name: rule(() => rm('product_kitchencontact_error_name'), required),
  phone: rule(() => rm('product_kitchencontact_error_phone'), isValidPhoneNumber),
  email: rule(() => rm('product_kitchencontact_error_email_incorrect'), isValidEmail)
};

const KitchenContactConfigurator = ({ setModal, fullProductId }: MergedProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const changeValue =
    (key: FormActionKey): OnChangeHandler =>
    e =>
      dispatch({ type: 'setValue', key, value: e.target.value });

  const handleSubmit: FormEventHandler = e => {
    e.preventDefault();
    const error = Object.keys(rules)
      .map((key: FormActionKey) => rules[key](state[key]))
      .find(x => x !== undefined);
    if (error) {
      dispatch({ type: 'setError', error });
      return;
    }
    const { email, name, phone } = state;
    dispatch({ type: 'setLoading' });
    return postKitchenContact({
      email,
      name,
      phone,
      productId: fullProductId
    })
      .then(() => {
        setModal(rm('product_kitchencontact_confirmation'));
        dispatch({ type: 'reset' });
      })
      .catch(() => {
        setModal(rm('product_kitchencontact_error_save'));
        dispatch({ type: 'resetLoading' });
      });
  };

  return (
    <Block>
      <p className={styles.info}>{rm('product_kitchencontact_body')}</p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.inputLabel}>
          {rm('product_kitchencontact_name')}
          <Input className={styles.input} value={state.name} onChange={changeValue('name')} />
        </label>
        <label className={styles.inputLabel}>
          {rm('product_kitchencontact_phone')}
          <Input
            className={styles.input}
            value={state.phone}
            onChange={changeValue('phone')}
            inputMode="tel"
          />
        </label>
        <label className={styles.inputLabel}>
          {rm('product_kitchencontact_email')}
          <Input className={styles.input} value={state.email} onChange={changeValue('email')} />
        </label>
        <div className={styles.submitArea}>
          <p className={styles.error}>{state.error}</p>
          <Button
            className={styles.button}
            loading={state.loadingStatus === LoadingStatus.Fetching}
            type="submit"
          >
            {rm('product_kitchencontact_submit')}
          </Button>
        </div>
      </form>
    </Block>
  );
};

export default connect<MergedProps, OwnProps>(state => ({
  fullProductId: getFullProductId(state.currentPage)
}))(KitchenContactConfigurator);
